// A form generated from the backend's field descriptions (GET /api/meta/resources).
import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea, Checkbox } from '@/components/ui/input';
import { Field, ErrorBox } from '@/components/common';
import { resourceHelpKey } from '@/help';
import { type FieldMeta, type ResourceMeta, useRefOptions, rowLabel } from '@/hooks/meta';
import { AudioFilePicker } from '@/components/AudioFilePicker';
import { TypedValueInput, DestinationStringInput, LanguageInput, typeAllowed } from '@/components/DestinationPickers';
import { PbxTargetInput, NumberListInput, MemberTargetInput } from '@/components/PbxPickers';
import { useAuth } from '@/store/auth';
import { fieldLabel, enumLabel } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { t } from '@/i18n';

type Values = Record<string, any>;

function RefSelect({ name, f, value, onChange }: { name: string; f: FieldMeta; value: any; onChange: (v: any) => void }) {
  const opts = useRefOptions(f.ref);
  return (
    <Select name={name} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
      <option value="">{f.required ? t('Choose…') : t('(none)')}</option>
      {(opts.data ?? []).map((o) => <option key={o.id} value={o.id}>{rowLabel(o)}</option>)}
    </Select>
  );
}

// 'ids': several rows of the referenced resource (e.g. a campaign's queues), as checkboxes
function RefMulti({ name, f, value, onChange }: { name: string; f: FieldMeta; value: any; onChange: (v: number[]) => void }) {
  const opts = useRefOptions(f.ref);
  const chosen = new Set<number>(Array.isArray(value) ? value.map(Number) : []);
  const rows = opts.data ?? [];
  return (
    <div role="group" aria-label={fieldLabel(name)} className="grid max-h-40 gap-1 overflow-y-auto rounded-md border p-2 text-sm">
      {rows.length === 0 && <span className="text-muted-foreground">{t('None yet')}</span>}
      {rows.map((o) => (
        <label key={o.id} className="flex items-center gap-2">
          <Checkbox name={`${name}[]`} value={o.id} checked={chosen.has(Number(o.id))}
            onChange={(e) => {
              const next = new Set(chosen);
              if (e.target.checked) next.add(Number(o.id)); else next.delete(Number(o.id));
              onChange([...next].sort((a, b) => a - b));
            }} />
          {rowLabel(o)}
        </label>
      ))}
    </div>
  );
}

// changing a *_type field makes its destination value meaningless (a queue id is not an agent code)
function clearDependents(fields: Record<string, FieldMeta>, changed: string, from: any, to: any): Values {
  if (from === to) return {};
  const out: Values = {};
  for (const [k, f] of Object.entries(fields)) if (f.kind === 'destination' && f.typeField === changed) out[k] = null;
  return out;
}

function FieldInput({ name, f, value, onChange, onChangeMany, editing, values, typeField }: { name: string; f: FieldMeta; value: any; onChange: (v: any) => void; onChangeMany: (v: Values) => void; editing: boolean; values: Values; typeField?: boolean }) {
  const tenant = useAuth((s) => s.tenant);
  // a value whose meaning depends on a sibling type field (inbound destinations, IVR option/action data)
  if (f.kind === 'destination') return <TypedValueInput name={name} type={f.typeField ? values[f.typeField] ?? null : null} value={value} onChange={onChange} max={f.max} />;
  if (f.kind === 'destination_string') return <DestinationStringInput name={name} value={value} onChange={onChange} exclude={f.excludeTypes} />;
  // PBX (docs/PBX.md): numbers are picked, not typed
  if (f.kind === 'pbx_target') return <PbxTargetInput name={name} value={value} onChange={onChange} targets={f.targets} />;
  if (f.kind === 'language') return <LanguageInput name={name} value={value} onChange={onChange} />;
  if (f.kind === 'number_list') return <NumberListInput name={name} value={value} onChange={onChange} />;
  if (f.kind === 'member_target' && f.pair) return <MemberTargetInput name={name} value={value} pairName={f.pair} pairValue={values[f.pair]} onChange={onChangeMany} />;
  if (f.kind === 'audio') return <AudioFilePicker name={name} value={value} onChange={onChange} required={f.required} />;
  if (f.type === 'ids') return <RefMulti name={name} f={f} value={value} onChange={onChange} />;
  if (f.ref) return <RefSelect name={name} f={f} value={value} onChange={onChange} />;
  switch (f.type) {
    case 'bool':
      return <Checkbox name={name} checked={!!Number(value) || value === true} onChange={(e) => onChange(e.target.checked)} />;
    case 'enum':
      return (
        <Select name={name} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
          {(!f.required || value === undefined) && <option value="">{f.nullable ? t('(none)') : t('Default')}</option>}
          {/* a destination type or IVR action of a product the tenant does not run is not offered (kept when already set) */}
          {f.values?.filter((v) => !typeField || v === value || typeAllowed(v, tenant)).map((v) => <option key={v} value={v}>{enumLabel(v)}</option>)}
        </Select>
      );
    case 'json':
      return <Textarea name={name} rows={3} className="font-mono text-xs" value={typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value)}
                       onChange={(e) => onChange(e.target.value)} placeholder='{"key": "value"}' />;
    case 'int': case 'num':
      return <Input name={name} type="number" step={f.type === 'num' ? 'any' : 1} min={f.min} max={f.max} value={value ?? ''}
                    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'time':
      return <Input name={name} type="time" step={1} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} />;
    case 'date':
      return <Input name={name} type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} />;
    case 'datetime':
      return <Input name={name} type="datetime-local" value={value ? String(value).replace(' ', 'T').slice(0, 16) : ''} onChange={(e) => onChange(e.target.value ? e.target.value.replace('T', ' ') + ':00' : null)} />;
    default:
      return <Input name={name} type={f.hidden ? 'password' : f.type === 'phone' ? 'tel' : 'text'} maxLength={f.max}
                    placeholder={f.hidden && editing ? t('unchanged') : undefined} autoComplete={f.hidden ? 'new-password' : 'off'}
                    value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} />;
  }
}

export function ResourceForm({ meta, initial, onSubmit, onCancel, fields, fixed }: {
  meta: ResourceMeta; initial?: Values | null; onSubmit: (v: Values) => Promise<void>; onCancel: () => void;
  fields?: string[]; fixed?: Values;
}) {
  const editing = !!initial;
  const names = (fields ?? Object.keys(meta.fields)).filter((k) => meta.fields[k] && !meta.fields[k].readOnly && !(fixed && k in fixed));
  const [values, setValues] = React.useState<Values>(() => {
    const v: Values = {};
    for (const k of names) if (initial && initial[k] !== undefined && !meta.fields[k].hidden) v[k] = meta.fields[k].type === 'json' && initial[k] && typeof initial[k] === 'object' ? JSON.stringify(initial[k]) : initial[k];
    return v;
  });
  const [error, setError] = React.useState<unknown>(null);
  const [busy, setBusy] = React.useState(false);
  // big forms (2026-10-21): only the resource's basic fields at first, the rest behind "Show advanced options"
  const basic = meta.basic ? new Set(meta.basic) : null;
  const isBasic = (k: string) => !basic || basic.has(k) || !!meta.fields[k].required;
  const advancedCount = names.filter((k) => !isBasic(k) && meta.fields[k].kind !== 'paired').length;
  const [advanced, setAdvanced] = React.useState(false);
  // enum fields that choose the type of a destination (inbound route types, IVR actions)
  const typeFields = new Set(Object.values(meta.fields).filter((f) => f.kind === 'destination' && f.typeField).map((f) => f.typeField!));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const out: Values = { ...(fixed ?? {}) };
    for (const k of names) {
      const f = meta.fields[k];
      let v = values[k];
      if (v === undefined) continue;
      if (f.hidden && (v === null || v === '') && editing) continue;            // keep the secret
      if (editing && !f.hidden && initial && JSON.stringify(initial[k]) === JSON.stringify(v)) continue;  // unchanged
      if (f.type === 'json' && typeof v === 'string') {
        try { v = v.trim() ? JSON.parse(v) : null; } catch { setError(new Error(t('{{field}}: not valid JSON', { field: fieldLabel(k) }))); return; }
      }
      if (f.type === 'bool') v = !!v;
      out[k] = v;
    }
    setBusy(true);
    try { await onSubmit(out); } catch (err) {
      setError(err);
      // the field the server complains about may be one of the hidden advanced fields
      if (err instanceof ApiError && typeof err.body?.field === 'string' && !isBasic(err.body.field)) setAdvanced(true);
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="grid gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {names.map((k) => {
          const f = meta.fields[k];
          if (f.when && !f.when.values.includes(values[f.when.field] ?? f.when.unset)) return null;
          if (f.kind === 'paired') return null;   // edited through its member_target field
          if (!advanced && advancedCount > 0 && !isBasic(k)) return null;
          return (
            <div key={k} className={f.type === 'json' ? 'sm:col-span-2' : ''}>
              <Field label={`${fieldLabel(k)}${f.required && !editing ? ' *' : ''}`} help={resourceHelpKey(meta.path, k)}>
                <FieldInput name={k} f={f} value={values[k]} editing={editing} values={values} typeField={typeFields.has(k)} onChange={(v) => setValues((s) => ({ ...s, [k]: v, ...clearDependents(meta.fields, k, s[k], v) }))}
                  onChangeMany={(many) => setValues((s) => ({ ...s, ...many }))} />
              </Field>
            </div>
          );
        })}
      </div>
      {advancedCount > 0 && (
        <div>
          <Button type="button" variant="ghost" size="sm" aria-expanded={advanced} data-testid="advanced-toggle" onClick={() => setAdvanced((a) => !a)}>
            {advanced ? <ChevronUp /> : <ChevronDown />}
            {advanced ? t('Hide advanced options') : t('Show advanced options ({{n}})', { n: advancedCount })}
          </Button>
        </div>
      )}
      <ErrorBox error={error} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>{t('Cancel')}</Button>
        <Button type="submit" disabled={busy}>{editing ? t('Save') : t('Create')}</Button>
      </div>
    </form>
  );
}
